import {
  ColumnFiltersState,
  PaginationState,
  Row,
  SortingState,
} from '@tanstack/react-table'
import { Button, Segmented, Spin } from 'antd'
import SearchBar from 'components/SearchBar'
import { TableComponent } from 'components/table/TableComponent'
import { ContextMenu } from 'features/knowledgeDocumentViewer/components/ContextMenu'
import { useAdvancedSearch } from 'features/knowledgeDocumentViewer/hooks/useAdvancedSearch'
import { useKeywordSearchPagination } from 'features/knowledgeDocumentViewer/hooks/useKeywordSearchPagination'
import { useRolodexEntriesCachedById } from 'features/knowledgeDocumentViewer/hooks/useRolodexEntriesCachedById'
import {
  DocumentTab,
  SearchHistoryItem,
  SearchMode,
} from 'features/knowledgeDocumentViewer/types'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { SystemDocumentEntry } from 'types'

import { DocumentPreviewPanel } from './DocumentPreviewPanel'
import {
  DocumentTableRow,
  transformDocumentsToTableData,
} from './DocumentTableColumns'
import { RecentDocuments } from './RecentDocuments'

const PageContainer = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
  background-color: ${(props) => props.theme.forecast.pageBackground1};
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`

const SearchAndRecentsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  gap: 0.5rem;
  flex-shrink: 0;
`

const SearchBarContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  flex: 1;
`

const Label = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${(props) => props.theme.forecast.defaultText};
  padding-top: 0.25rem;
  flex-shrink: 0;
`

const SemanticSearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
`

const StyledSearchBar = styled(SearchBar)`
  flex: 1;
`

const TableWrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 0.5rem 0;
`

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: ${(props) => props.theme.forecast.secondaryText};
  height: 100%;
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;
  color: ${(props) => props.theme.forecast.secondaryText};
  height: 100%;
`

const FeedbackFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 0.75rem 1rem;
  border-top: 0.0625rem solid ${(props) => props.theme.forecast.border};
  gap: 0.75rem;
`

const FeedbackCount = styled.span`
  color: ${(props) => props.theme.forecast.secondaryText};
  font-size: 0.875rem;
`

interface AdvancedSearchPageProps {
  onSelectDocument: (documentId: string) => void
  onOpenNewTab?: (documentId: string) => void
  advancedSearchTableData: DocumentTableRow[]
  siteId?: string
  isLoadingDocuments?: boolean
  searchState: SearchHistoryItem['searchState']
  onSearchStateChange: (
    updates: Partial<SearchHistoryItem['searchState']>
  ) => void
  recentDocuments: DocumentTableRow[]
  onAddRecentDocument: (document: DocumentTableRow) => void
  openTabs: DocumentTab[]
  onSwitchToTab: (tabId: string) => void
  activeTabId: string
  linkedDocumentIds?: string[]
}

export function AdvancedSearchPage({
  onSelectDocument,
  onOpenNewTab,
  advancedSearchTableData,
  siteId,
  isLoadingDocuments = false,
  searchState,
  onSearchStateChange,
  recentDocuments,
  onAddRecentDocument,
  openTabs,
  onSwitchToTab,
  activeTabId,
  linkedDocumentIds,
}: AdvancedSearchPageProps) {
  const [contextMenuPosition, setContextMenuPosition] = useState<{
    x: number
    y: number
  } | null>(null)
  const [contextMenuDocument, setContextMenuDocument] =
    useState<DocumentTableRow | null>(null)

  const {
    pagination,
    sorting,
    searchMode,
    query: searchInputValue,
    semanticQuery,
    filters: columnFilters,
    selectedDocumentId,
  } = searchState

  const {
    data: selectedDocumentEntries,
    isLoading: isLoadingSelectedDocument,
  } = useRolodexEntriesCachedById<SystemDocumentEntry>(
    selectedDocumentId ? [selectedDocumentId] : []
  )

  const selectedDocument = useMemo(() => {
    if (!selectedDocumentEntries?.[0]) return null
    return transformDocumentsToTableData(selectedDocumentEntries)[0] ?? null
  }, [selectedDocumentEntries])

  const setPagination = useCallback(
    (
      updater: PaginationState | ((prev: PaginationState) => PaginationState)
    ) => {
      const newPagination =
        typeof updater === 'function' ? updater(pagination) : updater
      onSearchStateChange({ pagination: newPagination })
    },
    [pagination, onSearchStateChange]
  )

  const setSorting = useCallback(
    (updater: SortingState | ((prev: SortingState) => SortingState)) => {
      const newSorting =
        typeof updater === 'function' ? updater(sorting) : updater
      onSearchStateChange({ sorting: newSorting })
    },
    [sorting, onSearchStateChange]
  )

  const setSearchMode = useCallback(
    (updater: SearchMode | ((prev: SearchMode) => SearchMode)) => {
      const newSearchMode =
        typeof updater === 'function' ? updater(searchMode) : updater
      onSearchStateChange({ searchMode: newSearchMode })
    },
    [searchMode, onSearchStateChange]
  )

  const setSearchInputValue = useCallback(
    (updater: string | ((prev: string) => string)) => {
      const newValue =
        typeof updater === 'function' ? updater(searchInputValue) : updater
      onSearchStateChange({ query: newValue })
    },
    [searchInputValue, onSearchStateChange]
  )

  const setSemanticQuery = useCallback(
    (updater: string | ((prev: string) => string)) => {
      const newValue =
        typeof updater === 'function' ? updater(semanticQuery) : updater
      onSearchStateChange({ semanticQuery: newValue })
    },
    [semanticQuery, onSearchStateChange]
  )

  const setColumnFilters = useCallback(
    (
      updater:
        | ColumnFiltersState
        | ((prev: ColumnFiltersState) => ColumnFiltersState)
    ) => {
      const newFilters =
        typeof updater === 'function' ? updater(columnFilters) : updater
      onSearchStateChange({ filters: newFilters })
    },
    [columnFilters, onSearchStateChange]
  )

  const {
    data: paginatedKeywordData,
    totalCount: paginatedTotalCount,
    isLoading: isLoadingPaginated,
  } = useKeywordSearchPagination({
    siteId,
    searchText: searchInputValue,
    columnFilters,
    sorting,
    pagination,
    linkedDocumentIds,
  })

  const {
    feedbackSelectedIds,
    isSemanticLoading,
    isLoadingPaginatedData,
    showSemanticSearchEmptyState,
    tableData,
    tableColumns,
    paginatedKeywordTotalCount,
    handleSearchChange,
    handleSemanticQueryChange,
    handleSemanticKeyDown,
    handleSemanticSearch,
    handleSearchModeChange,
    handleSubmitFeedback,
    handleDocumentSelect,
    handleFilteredDataChange,
  } = useAdvancedSearch({
    advancedSearchTableData,
    siteId,
    initialGlobalFilter: searchInputValue,
    initialColumnFilters: columnFilters,
    pagination,
    setPagination,
    onSelectDocument,
    searchMode,
    setSearchMode,
    searchInputValue,
    setSearchInputValue,
    semanticQuery,
    setSemanticQuery,
    columnFilters,
    setColumnFilters,
    paginatedKeywordData,
    paginatedKeywordTotalCount: paginatedTotalCount,
    isLoadingPaginatedData: isLoadingPaginated,
  })

  const handleRowKeyDown = useCallback(
    ({
      event,
      rowElement,
    }: {
      event: React.KeyboardEvent<HTMLTableRowElement>
      row: Row<DocumentTableRow>
      rowElement: HTMLTableRowElement
    }) => {
      event.stopPropagation()

      switch (event.key) {
        case 'ArrowUp': {
          event.preventDefault()
          const prevRow = rowElement.previousElementSibling as HTMLElement
          if (prevRow) {
            prevRow.focus()
            prevRow.scrollIntoView({ block: 'nearest' })
          }
          break
        }
        case 'ArrowDown': {
          event.preventDefault()
          const nextRow = rowElement.nextElementSibling as HTMLElement
          if (nextRow) {
            nextRow.focus()
            nextRow.scrollIntoView({ block: 'nearest' })
          }
          break
        }
        case 'Enter': {
          if (event.metaKey || event.ctrlKey) {
            if (onOpenNewTab && selectedDocument) {
              onOpenNewTab(selectedDocument.id)
            }
          } else if (selectedDocument) {
            handleDocumentSelect(selectedDocument)
          }
          break
        }
        default:
          break
      }
    },
    [handleDocumentSelect, onOpenNewTab, selectedDocument]
  )

  const handleRowFocus = useCallback(
    (row: Row<DocumentTableRow>) => {
      onSearchStateChange({
        selectedDocumentId: row.original.id,
      })
    },
    [onSearchStateChange]
  )
  const handleClosePreview = useCallback(() => {
    onSearchStateChange({
      selectedDocumentId: null,
    })
  }, [onSearchStateChange])

  const handleOpenDocument = useCallback(
    (doc: DocumentTableRow) => {
      onAddRecentDocument(doc)
      handleDocumentSelect(doc)
    },
    [handleDocumentSelect, onAddRecentDocument]
  )

  const handleRecentDocumentSelect = useCallback(
    (doc: DocumentTableRow, event?: React.MouseEvent) => {
      if (event && event.detail === 2) {
        const forceNewTab = event.metaKey || event.ctrlKey

        const existingTab = openTabs.find((tab) => {
          const currentItem = tab.history[tab.currentIndex]
          return (
            currentItem.type === 'document' && currentItem.documentId === doc.id
          )
        })

        if (existingTab && !forceNewTab) {
          onSwitchToTab(existingTab.id)
          return
        }

        onAddRecentDocument(doc)

        if (forceNewTab && onOpenNewTab) {
          onOpenNewTab(doc.id)
        } else {
          handleDocumentSelect(doc)
        }
        return
      }

      onSearchStateChange({
        selectedDocumentId: doc.id,
      })
    },
    [
      handleDocumentSelect,
      onAddRecentDocument,
      openTabs,
      onSwitchToTab,
      onOpenNewTab,
      onSearchStateChange,
    ]
  )

  const handleOpenInNewTab = useCallback(
    (doc: DocumentTableRow) => {
      if (onOpenNewTab) {
        onAddRecentDocument(doc)
        onOpenNewTab(doc.id)
      }
    },
    [onOpenNewTab, onAddRecentDocument]
  )

  const handleRowClick = useCallback(
    (
      row: Row<DocumentTableRow>,
      event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
    ) => {
      onSearchStateChange({
        selectedDocumentId: row.original.id,
      })

      if (event.detail === 2) {
        if (event.ctrlKey || event.metaKey) {
          handleOpenInNewTab(row.original)
        } else {
          handleOpenDocument(row.original)
        }
      }
    },
    [handleOpenDocument, handleOpenInNewTab, onSearchStateChange]
  )

  const handleRowAuxClick = useCallback(
    (
      row: Row<DocumentTableRow>,
      event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
    ) => {
      if (event.button === 1 && onOpenNewTab) {
        event.preventDefault()
        onAddRecentDocument(row.original)
        onOpenNewTab(row.original.id)
      }
    },
    [onOpenNewTab, onAddRecentDocument]
  )

  const handleRowContextMenu = useCallback(
    (
      row: Row<DocumentTableRow>,
      event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
    ) => {
      event.preventDefault()
      setContextMenuDocument(row.original)
      setContextMenuPosition({ x: event.clientX, y: event.clientY })
    },
    []
  )

  const handleRecentContextMenu = useCallback(
    (doc: DocumentTableRow, event: React.MouseEvent) => {
      event.preventDefault()
      setContextMenuDocument(doc)
      setContextMenuPosition({ x: event.clientX, y: event.clientY })
    },
    []
  )

  const handleContextMenuClose = useCallback(() => {
    setContextMenuPosition(null)
    setContextMenuDocument(null)
  }, [])

  const handleContextMenuOpen = useCallback(() => {
    if (contextMenuDocument) {
      handleOpenDocument(contextMenuDocument)
    }
  }, [contextMenuDocument, handleOpenDocument])

  const handleContextMenuOpenInNewTab = useCallback(() => {
    if (contextMenuDocument && onOpenNewTab) {
      onAddRecentDocument(contextMenuDocument)
      onOpenNewTab(contextMenuDocument.id)
    }
  }, [contextMenuDocument, onOpenNewTab, onAddRecentDocument])

  if (isLoadingDocuments && searchMode === 'semantic') {
    return (
      <PageContainer>
        <ContentContainer>
          <LoadingContainer>
            <Spin size='large' />
            <p>Loading documents...</p>
          </LoadingContainer>
        </ContentContainer>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <ContentContainer>
        <SearchAndRecentsContainer>
          {recentDocuments.length > 0 && <Label>Recents</Label>}
          <SearchBarContainer>
            <Segmented
              options={[
                { label: 'Keyword', value: 'keyword' },
                { label: 'Semantic', value: 'semantic' },
              ]}
              value={searchMode}
              onChange={handleSearchModeChange}
            />
            {searchMode === 'keyword' ? (
              <SearchBar
                value={searchInputValue}
                onChange={handleSearchChange}
                placeholder='Search documents...'
              />
            ) : (
              <SemanticSearchContainer>
                <StyledSearchBar
                  value={semanticQuery}
                  onChange={handleSemanticQueryChange}
                  onKeyDown={handleSemanticKeyDown}
                  placeholder='Enter semantic search query...'
                />
                <Button
                  type='primary'
                  onClick={handleSemanticSearch}
                  loading={isSemanticLoading}
                  disabled={!semanticQuery.trim()}>
                  Search
                </Button>
              </SemanticSearchContainer>
            )}
          </SearchBarContainer>
        </SearchAndRecentsContainer>
        <RecentDocuments
          documents={recentDocuments}
          selectedDocumentId={selectedDocumentId}
          onSelectDocument={handleRecentDocumentSelect}
          onContextMenu={onOpenNewTab ? handleRecentContextMenu : undefined}
        />
        <TableWrapper>
          {isSemanticLoading ? (
            <EmptyStateContainer>
              <Spin size='large' />
              <p>Searching documents...</p>
            </EmptyStateContainer>
          ) : showSemanticSearchEmptyState ? (
            <EmptyStateContainer>
              <p>No documents found matching your query.</p>
            </EmptyStateContainer>
          ) : (
            <TableComponent
              key={activeTabId}
              cardStyleRows={false}
              columns={tableColumns}
              data={tableData}
              isLoadingData={
                searchMode === 'keyword'
                  ? isLoadingPaginatedData
                  : isSemanticLoading
              }
              footerName='Documents'
              pagination={pagination}
              setPagination={setPagination}
              sorting={sorting}
              setSorting={setSorting}
              showTable={true}
              onRowClick={handleRowClick}
              onRowAuxClick={handleRowAuxClick}
              onRowContextMenu={handleRowContextMenu}
              onRowKeyDown={handleRowKeyDown}
              onRowFocus={handleRowFocus}
              showFilterBar={searchMode === 'keyword'}
              disableUrlFilters={true}
              showFooter={true}
              isTableHeaderSticky={true}
              showColumnConfig={false}
              cellPadding='.25rem'
              cellFontSize='smaller'
              borderSpacing='0'
              onFilteredDataChange={
                searchMode === 'keyword' ? handleFilteredDataChange : undefined
              }
              initialColumnFilters={
                searchMode === 'keyword' ? columnFilters : undefined
              }
              highlightSelectedRow
              manualPagination={searchMode === 'keyword'}
              totalRowCount={
                searchMode === 'keyword'
                  ? paginatedKeywordTotalCount
                  : undefined
              }
              manualFiltering={searchMode === 'keyword'}
              manualSorting={searchMode === 'keyword'}
              getRowId={(row) => row.id}
              controlledHighlightedRowId={selectedDocumentId ?? null}
            />
          )}
        </TableWrapper>
        {searchMode === 'semantic' && feedbackSelectedIds.size > 0 && (
          <FeedbackFooter>
            <FeedbackCount>
              {feedbackSelectedIds.size} document
              {feedbackSelectedIds.size !== 1 ? 's' : ''} marked as relevant
            </FeedbackCount>
            <Button type='primary' onClick={handleSubmitFeedback}>
              Submit Feedback
            </Button>
          </FeedbackFooter>
        )}
      </ContentContainer>
      <DocumentPreviewPanel
        document={selectedDocument}
        isOpen={!!selectedDocumentId}
        isLoading={isLoadingSelectedDocument}
        onClose={handleClosePreview}
        onOpen={handleOpenDocument}
        onOpenNewTab={onOpenNewTab ? handleOpenInNewTab : undefined}
      />
      {onOpenNewTab && (
        <ContextMenu
          position={contextMenuPosition}
          onClose={handleContextMenuClose}
          onOpen={handleContextMenuOpen}
          onOpenInNewTab={handleContextMenuOpenInNewTab}
        />
      )}
    </PageContainer>
  )
}
